<template>
  <div>
    <b-button-group class="mb-3">
      <b-button
        v-for="(tab, tabKey, index) in config"
        :key="`tab-${tabKey}`"
        :variant="tabVariant(tab, tabKey, index)"
        @click="switchTab(tabKey)"
      >
        {{ tab.title }}
      </b-button>
    </b-button-group>

    <ul
      v-for="tabKey in tabKeys"
      v-show="currentTabKey === tabKey"
      :key="`list-${tabKey}`"
      v-infinite-scroll="loadMore"
      :class="[
        'list-group overflow-y-auto',
        listClass,
        `${name}-list`,
        `${name}-list-${tabKey}`,
        $lodash.get(config[tabKey], 'tabListClass'),
      ]"
      infinite-scroll-disabled="scrollLoading"
      infinite-scroll-distance="30"
      :style="`max-height: ${height}px`"
    >
      <li
        v-for="listItem in currentList"
        :key="`list-${tabKey}-${listItem.id}`"
        class="list-group-item highlight"
        :class="[
          listItem.id === selectedItemId ? 'active' : '',
          altselectedItemIds.includes(listItem.id) ? 'alt-selected' : '',
        ]"
        @click.left.exact="selected(listItem)"
        @click.alt.left.exact.prevent="altSelected(listItem)"
      >
        <slot :item="listItem" :selectedItemId="selectedItemId"></slot>
      </li>
      <li v-if="scrollLoading" class="list-group-item text-center py-4">
        <loader color="#37a9e1" size="20px"></loader>
      </li>
    </ul>
  </div>
</template>

<script type="text/babel">
import loader from "vue-spinner/src/ClipLoader.vue";

export default {
  name: "TabbedList",

  components: {
    loader,
  },

  props: {
    name: {
      type: String,
      required: true,
    },

    initialTab: {
      type: String,
      default: null,
      required: false,
    },

    listClass: {
      type: String,
      default: "",
      required: false,
    },

    height: {
      type: Number,
      default: 500,
    },

    config: {
      type: Object,
      required: true,
      default: () => ({}),
    },

    selectValidator: {
      type: Function,
    },

    altSelectValidator: {
      type: Function,
    },
  },

  data() {
    return {
      initialised: false,
      tabData: {},
      currentTabKey: null,

      scrollLoading: false,
      scrollInitialLoaded: false,

      selectedItemId: null,
      altselectedItemIds: [],
    };
  },

  computed: {
    tabKeys() {
      return Object.keys(this.config);
    },

    currentTabData() {
      if (
        this.initialised &&
        this.currentTabKey &&
        this.tabData &&
        this.tabData[this.currentTabKey] &&
        this.tabData[this.currentTabKey].currentPage
      ) {
        return this.tabData[this.currentTabKey];
      }

      return null;
    },

    currentList: {
      cache: false,
      get() {
        if (this.currentTabData && this.currentTabData.list) {
          return this.currentTabData.list;
        }

        return [];
      },
    },
  },

  async mounted() {
    await this.init();
  },

  methods: {
    async init() {
      this.initialised = false;
      this.scrollLoading = true;

      this.resetTab();

      let response;

      for (const key in this.config) {
        response = await this.$http.get(this.config[key].url);

        this.tabData[key] = {
          list: response.body.data,
          total: response.body.total,
          currentPage: response.body.current_page,
          lastPage: response.body.last_page,
        };
      }

      this.scrollLoading = false;
      this.initialised = true;

      // Delay setting infinate scroll free
      setTimeout(() => {
        this.scrollInitialLoaded = true;
      }, 10);
    },

    tabVariant(tab, tabKey, index) {
      if (index === 0 && this.currentTabKey === null) {
        return tab.variant;
      }

      return this.currentTabKey === tabKey ? tab.variant : "";
    },

    switchTab(tabKey) {
      this.currentTabKey = tabKey;

      return this.$emit("tabbed", tabKey);
    },

    resetTab() {
      this.currentTabKey = this.tabKeys[0];
      if (this.initialTab) {
        this.currentTabKey = this.initialTab;
      }
    },

    resetSelected() {
      this.selectedItemId = null;
    },

    resetAltSelected() {
      this.altselectedItemIds = [];
    },

    async loadMore() {
      if (!this.scrollInitialLoaded) {
        return;
      }

      if (this.currentTabData.currentPage < this.currentTabData.lastPage) {
        this.scrollLoading = true;
        const page = this.currentTabData.currentPage + 1;

        const response = await this.$http.get(
          `${this.config[this.currentTabKey].url}&page=${page}`
        );

        const list = this.tabData[this.currentTabKey].list.concat(response.body.data);
        this.$set(this.tabData[this.currentTabKey], "list", list);
        this.$set(this.tabData[this.currentTabKey], "total", response.body.total);
        this.$set(this.tabData[this.currentTabKey], "currentPage", response.body.current_page);
        this.$set(this.tabData[this.currentTabKey], "lastPage", response.body.last_page);

        this.scrollLoading = false;
      }
    },

    selected(item) {
      if (this.selectValidator && !this.selectValidator(item)) {
        return;
      }

      this.$emit("selected", item);
      this.selectedItemId = item.id;
    },

    altSelected(item) {
      if (this.altSelectValidator && !this.altSelectValidator(item)) {
        return;
      }

      this.$emit("altSelected", item);

      if (this.altselectedItemIds.includes(item.id)) {
        this.altselectedItemIds = this.altselectedItemIds.filter((id) => id !== item.id);
        return;
      }
      this.altselectedItemIds.push(item.id);
    },
  },
};
</script>
